import { ConfirmDialog, NewConvergeButton } from '../../../presentation';
import {
  SubmissionStatus,
  useUpsertSubmissionMutation,
} from '@converge/graphql';
import { useCallback, useState } from 'react';

import { useConvergeSnackBar } from '../../../contexts';

interface UpdateSubmissionStatusButtonProps {
  submissionUuid: string;
  type: ActionType;
  label: string;
  title?: string;
}

export enum ActionType {
  INDICATE = 'INDICATE',
  REQUEST_MORE_INFO = 'REQUEST_MORE_INFO',
}

export const UpdateSubmissionStatusButton = ({
  type,
  label,
  title,
  submissionUuid,
}: UpdateSubmissionStatusButtonProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { notify } = useConvergeSnackBar();

  const { upsertSubmission } = useUpsertSubmissionMutation({}, 'Overview');

  const updateSubmission = useCallback(
    (submissionUuid: string) => {
      return upsertSubmission({
        variables: {
          details: {
            uuid: submissionUuid ?? '',
            status:
              type === ActionType.INDICATE
                ? SubmissionStatus.Indicated
                : SubmissionStatus.RequestedMoreInfo,
          },
        },
      });
    },
    [upsertSubmission, type]
  );

  const content = `Are you sure you want to ${
    type === ActionType.INDICATE
      ? 'indicate this submission'
      : 'request more info'
  }?`;

  return (
    <>
      <NewConvergeButton
        loading={isLoading}
        variant="contained"
        onClick={() => setShowConfirmDialog(true)}
        label={label}
      />
      <ConfirmDialog
        show={showConfirmDialog}
        title={title ?? 'Request more info'}
        content={content}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={async () => {
          try {
            setShowConfirmDialog(false);
            setIsLoading(true);
            await updateSubmission(submissionUuid);
            setIsLoading(false);
          } catch (cause) {
            setIsLoading(false);
            notify(
              cause instanceof Error
                ? `Error saving: ${cause.message}`
                : 'Error saving',
              {
                variant: 'error',
              }
            );
          }
        }}
      />
    </>
  );
};
